import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, post, patch, del } from '@/lib/api'

// ==================== Types ====================

export interface Position {
  id: string
  name: string
  code: string
  departmentId: string | null
  level: number | null
  description: string | null
  sortOrder: number | null
  isActive: boolean | null
  createdAt: string
  updatedAt: string | null
  deletedAt: string | null
}

export interface PositionWithDetails extends Position {
  department?: {
    id: string
    name: string
    code: string
  } | null
  employeeCount?: number
}

export interface FlatPosition {
  id: string
  name: string
  code: string
  level: number | null
}

export interface PositionListParams {
  departmentId?: string
  level?: number
  isActive?: boolean
  search?: string
}

export interface CreatePositionInput {
  name: string
  code: string
  departmentId?: string
  level?: number
  description?: string
  sortOrder?: number
}

export interface UpdatePositionInput {
  name?: string
  code?: string
  departmentId?: string | null
  level?: number
  description?: string | null
  sortOrder?: number
  isActive?: boolean
}

// ==================== Query Keys ====================

export const positionKeys = {
  all: ['positions'] as const,
  lists: () => [...positionKeys.all, 'list'] as const,
  list: (params: PositionListParams) => [...positionKeys.lists(), params] as const,
  flat: () => [...positionKeys.lists(), 'flat'] as const,
  details: () => [...positionKeys.all, 'detail'] as const,
  detail: (id: string) => [...positionKeys.details(), id] as const,
}

// ==================== Hooks ====================

/**
 * 获取职位列表
 */
export function usePositionList(params: PositionListParams = {}) {
  return useQuery({
    queryKey: positionKeys.list(params),
    queryFn: () => get<PositionWithDetails[]>('/hr/positions', params as Record<string, unknown>),
  })
}

/**
 * 获取职位扁平列表（下拉选择用）
 */
export function usePositions() {
  return useQuery({
    queryKey: positionKeys.flat(),
    queryFn: () => get<FlatPosition[]>('/hr/positions/flat'),
  })
}

/**
 * 获取单个职位详情
 */
export function usePosition(id: string) {
  return useQuery({
    queryKey: positionKeys.detail(id),
    queryFn: () => get<PositionWithDetails>(`/hr/positions/${id}`),
    enabled: !!id,
  })
}

/**
 * 创建职位
 */
export function useCreatePosition() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreatePositionInput) => post<Position>('/hr/positions', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: positionKeys.lists() })
    },
  })
}

/**
 * 更新职位
 */
export function useUpdatePosition() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePositionInput }) =>
      patch<Position>(`/hr/positions/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: positionKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: positionKeys.lists() })
    },
  })
}

/**
 * 删除职位
 */
export function useDeletePosition() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => del<null>(`/hr/positions/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: positionKeys.lists() })
    },
  })
}
